import { LabeledValue, Modal } from "@msys/ui";
import { Unit } from "@msys/units";
import { Alert, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { capitalize, merge, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  DefineItemProps2Entry,
  DocType,
  PermissionName,
  Props2,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import { type DocumentByType } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { AskIfConditionExpressionField } from "../fields/AskIfConditionExpressionField.js";
import { useAllowedValuesFieldValidationSchema } from "../form-fields/PropertyAllowedValuesField.js";
import { PropertyAllowedValuesFieldFromTemplateType } from "../form-fields/PropertyAllowedValuesFieldFromTemplateType.js";
import {
  getValuesMediaBool,
  useValuesMediaBoolFieldValidationSchema,
  ValuesMediaBoolField,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import {
  NumberRangeSection,
  useNumberRangeValidationSchemaWithTemplateType,
} from "../NumberRangeSection.js";
import {
  getAllowedValues,
  getPropertyType,
  getPropValues,
  getTemplateTypePropValues,
  isArrayType,
  isBooleanType,
  isNonComputedProp,
  isNumberType,
  isSameTypeForAllowedValues,
  isTextType,
  PROP_VALUES,
} from "../properties.js";
import {
  QuestionControlSection,
  useQuestionControlValidationSchema,
} from "../QuestionControlSection.js";
import { usePropertyUnits } from "../usePropertyUnits.js";
import { createPropertyEntry, FormValues } from "./PropertyEditModal.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  property: Props2;
  templateTypeProperty: Props2;
  handleClose: () => void;
  handleComplete: (changedProperty: DefineItemProps2Entry) => Promise<void>;
  docType: DocType;
  documentViewerPermissions: PermissionName[];
}

export function PropertyEditWithTemplateTypeModal({
  projectId,
  docId,
  itemId,
  property,
  templateTypeProperty,
  handleClose,
  handleComplete,
  docType,
  documentViewerPermissions,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { getUnits } = usePropertyUnits();
  const unitsOptions = React.useMemo(() => getUnits(), [getUnits]);
  const [error, setError] = React.useState<Error | null>(null);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const initialValues = React.useMemo(
    (): FormValues =>
      getInitialValuesWithTemplateType(
        property,
        templateTypeProperty,
        unitsOptions
      ),
    [property, templateTypeProperty, unitsOptions]
  );

  const propValues = React.useMemo(() => getPropValues(property), [property]);
  const templateTypePropValues = React.useMemo(
    () => getTemplateTypePropValues(templateTypeProperty, property),
    [templateTypeProperty, property]
  );

  const allowedValuesSchema = useAllowedValuesFieldValidationSchema(propValues);
  const valuesMediaBoolSchema = useValuesMediaBoolFieldValidationSchema();
  const questionControlSchema = useQuestionControlValidationSchema();
  const numberRangeSchema = useNumberRangeValidationSchemaWithTemplateType(
    templateTypePropValues
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        label: Yup.string()
          .required()
          .min(1)
          .label(t("Name", { ns: "Global" })),
        type: Yup.string().oneOf(PROP_VALUES).required(),
        essential: Yup.boolean().required(),
        customizable: Yup.boolean().required(),
        unit: Yup.object().shape({ key: Yup.string().required() }).nullable(),
        group: Yup.string(),
        ...allowedValuesSchema,
        ...valuesMediaBoolSchema,
        ...questionControlSchema,
        ...numberRangeSchema,
      }),
    [
      t,
      allowedValuesSchema,
      valuesMediaBoolSchema,
      questionControlSchema,
      numberRangeSchema,
    ]
  );

  const onSubmit = async (values: FormValues) => {
    try {
      await handleComplete(createPropertyEntry(values, property));
      handleClose();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(JSON.stringify(e));
      setError(error);
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {formikProps => (
        <Modal
          title={t("Edit property", { ns: "QuoteItem" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Confirm", { ns: "Global" }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              {error && <Alert severity="error">{error.message}</Alert>}

              <LabeledValue label={t("Key", { ns: "QuoteItem" })}>
                {property.key}
              </LabeledValue>

              <TextField
                name={"label"}
                label={t("Label", { ns: "Global" })}
                autoFocus
              />

              <LabeledValue label={t("Type", { ns: "QuoteItem" })}>
                {capitalize(formikProps.values.type)}
              </LabeledValue>

              {formikProps.values.type === "NUMBER" && (
                <LabeledValue
                  label={t("Unit", { ns: "QuoteItem" })}
                  notSet={!formikProps.values.unit?.value}
                >
                  {formikProps.values.unit?.value ||
                    t("Not set", { ns: "Global" })}
                </LabeledValue>
              )}

              {formikProps.values.type !== "BOOLEAN" && (
                <CheckboxField
                  name={"isArray"}
                  label={t("Allow multiple values", {
                    ns: "QuoteItem",
                  })}
                  disabled
                />
              )}

              {formikProps.values.type !== "BOOLEAN" &&
                isNonComputedProp(property) && (
                  <PropertyAllowedValuesFieldFromTemplateType
                    type={formikProps.values.type}
                    disabled={
                      (formikProps.values.type === "NUMBER" &&
                        (formikProps.values.setRange ||
                          formikProps.values.setStep)) ||
                      formikProps.isSubmitting
                    }
                    templateTypeAllowedValues={
                      templateTypePropValues.templateTypeAllowedValues
                    }
                    excludedType={templateTypePropValues.propertyType}
                    excludedAllowedValues={
                      templateTypePropValues.propertyExcludedAllowedValues
                    }
                  />
                )}
              {formikProps.values.type === "BOOLEAN" &&
                isNonComputedProp(property) && (
                  <ValuesMediaBoolField disabled={formikProps.isSubmitting} />
                )}

              {formikProps.values.type === "NUMBER" &&
                isNonComputedProp(property) && (
                  <NumberRangeSection
                    disabledSetRange={templateTypePropValues.hasDefinedRange}
                    disabledSetStep={templateTypePropValues.hasDefinedStep}
                    disabled={
                      formikProps.values.defineAllowedValues ||
                      formikProps.isSubmitting
                    }
                  />
                )}
              <TextField
                name={"group"}
                label={t("Group", { ns: "QuoteItem" })}
              />
              <CheckboxField
                name={`clientVisibility`}
                label={t("Visibility", { ns: "Global" })}
              />
              {docType === "TEMPLATE" && (
                <RestrictedByCapabilityWithDebug capability="TEMPLATE_SHARING">
                  <CheckboxField
                    name="customizable"
                    label={t("Customisable", { ns: "QuoteItem" })}
                  />
                </RestrictedByCapabilityWithDebug>
              )}
              {isNonComputedProp(property) && (
                <QuestionControlSection isInitiallyExpanded />
              )}
              {isNonComputedProp(property) && (
                <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                  capability="EXPRESSIONS"
                  permissionByType={{
                    QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                    REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                    TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                    BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                  }}
                  document={document}
                  otherwise={
                    <AskIfConditionExpressionField
                      projectId={projectId}
                      itemId={itemId}
                      docId={docId}
                      name={"askIfExpr"}
                      label={t("Ask if condition", { ns: "QuoteItem" })}
                      addButtonLabel={t("Add condition", { ns: "QuoteItem" })}
                      currentItemId={itemId}
                      currentPropertyKey={property.key}
                      disabled={true}
                      readOnly={true}
                    />
                  }
                >
                  <AskIfConditionExpressionField
                    projectId={projectId}
                    itemId={itemId}
                    docId={docId}
                    name={"askIfExpr"}
                    label={t("Ask if condition", { ns: "QuoteItem" })}
                    addButtonLabel={t("Add condition", { ns: "QuoteItem" })}
                    currentItemId={itemId}
                    currentPropertyKey={property.key}
                    disabled={formikProps.isSubmitting}
                  />
                </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
              )}
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
}

export function getInitialValuesWithTemplateType(
  property: Props2,
  templateTypeProperty: Props2,
  unitsOptions: Unit[]
) {
  const templateTypeCanHaveRangeOrStep =
    isNumberType(templateTypeProperty) &&
    isNonComputedProp(templateTypeProperty);

  const templateTypeHasDefinedRange =
    templateTypeCanHaveRangeOrStep && Boolean(templateTypeProperty.range);

  const templateTypeHasDefinedStep =
    templateTypeCanHaveRangeOrStep && Boolean(templateTypeProperty.step);

  const propertyHasDefinedRange =
    isNumberType(property) &&
    isNonComputedProp(property) &&
    Boolean(property.range);

  const propertyHasDefinedStep =
    isNumberType(property) &&
    isNonComputedProp(property) &&
    Boolean(property.step);

  const templateTypeHasAllowedValues =
    isNonComputedProp(templateTypeProperty) &&
    ((isTextType(templateTypeProperty) &&
      templateTypeProperty.allowedValuesText.length > 0) ||
      (isNumberType(templateTypeProperty) &&
        templateTypeProperty.allowedValuesNumber.length > 0));

  const propertyHasAllowedValues =
    isNonComputedProp(property) &&
    ((isTextType(property) && property.allowedValuesText.length > 0) ||
      (isNumberType(property) && property.allowedValuesNumber.length > 0));

  const propertyHasValuesMediaBool =
    isNonComputedProp(property) &&
    isBooleanType(property) &&
    property.valuesMediaBool.length > 0;

  const templateTypeAllowedValues = getAllowedValues(templateTypeProperty);
  const propertyAllowedValues = getAllowedValues(property);

  return {
    label: property.label,
    type: getPropertyType(templateTypeProperty),
    defineAllowedValues:
      templateTypeHasAllowedValues || propertyHasAllowedValues,
    allowedValues:
      (templateTypeHasAllowedValues || propertyHasAllowedValues) &&
      isSameTypeForAllowedValues(templateTypeProperty, property)
        ? templateTypeAllowedValues.filter(tav =>
            propertyAllowedValues.some(av => av.value === tav.value)
          )
        : getAllowedValues(property),
    defineValuesMediaBool: propertyHasValuesMediaBool,
    valuesMediaBool: getValuesMediaBool(property),
    setRange: templateTypeHasDefinedRange || propertyHasDefinedRange,
    setStep: templateTypeHasDefinedStep || propertyHasDefinedStep,
    range:
      templateTypeHasDefinedRange || propertyHasDefinedRange
        ? merge(
            { min: null, max: null },
            {
              min:
                isNumberType(property) && isNonComputedProp(property)
                  ? property.range?.min
                  : null,
              max:
                isNumberType(property) && isNonComputedProp(property)
                  ? property.range?.max
                  : null,
            }
          )
        : { min: null, max: null },
    step:
      templateTypeHasDefinedStep || propertyHasDefinedStep
        ? isNumberType(property) && isNonComputedProp(property)
          ? (property.step ?? null)
          : null
        : null,
    essential: false,
    unit:
      isNumberType(templateTypeProperty) && templateTypeProperty.unit
        ? (unitsOptions.find(
            option => option.key === templateTypeProperty.unit
          ) ?? null)
        : null,
    group: property.group,
    clientVisibility: property.clientVisibility,
    prompt: isNonComputedProp(property) ? property.prompt : "",
    questionControl: isNonComputedProp(property)
      ? property.questionControl
      : [],
    isArray: isArrayType(templateTypeProperty),
    askIfExpr: "askIfExpr" in property ? property.askIfExpr : "",
    customizable: isNonComputedProp(property) ? property.customizable : false,
  };
}
